// Copyright (c) Docugami, Inc. All rights reserved.

import { Icon, Stack, TooltipHost } from "@fluentui/react";
import { GrabHandleContainer, cardShadow } from "components/cards/CardStyles";
import { CardTextClassName, CardTextContent } from "components/cards/CardTextContent";
import { ICommandButton } from "components/cards/CommandButton";
import { DndChunk, DndType } from "components/cards/DndTypes";
import { userFontFamily } from "components/common/Fonts";
import { appContentColor, chunkSelectedColor, greyText, primaryTheme } from "components/shell/presentation/AppStyles";
import { Property } from "hooks/documentProperties";
import { useEffect } from "react";
import { ConnectDragSource, useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import styled from "styled-components";

const CardLabel = styled.div`
    font-family: ${userFontFamily};
    font-size: 14px;
    font-weight: 700;
    line-height: 16px;
    margin-bottom: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
`

interface PropertyItemProps {
    property: Property;
    isSelected: boolean;
    onClick: () => void;
    onDelete: () => void;
    onEdit: () => void;
    isDraggable: boolean;
    onDragStart?: () => void;
    onDragEnd?: () => void;
}

export const PropertyItem = (props: PropertyItemProps): JSX.Element => {
    const { onDragEnd } = props;

    const [{ isDragging }, dragRef, dragVisualRef] = useDrag<DndChunk, any, { isDragging: boolean }>({
        type: DndType.Chunk,
        item: {
            type: DndType.Chunk,
            instance: props.property.chunk!,
            label: props.property.label!
        },
        canDrag: () => props.isDraggable,
        collect: m => ({
            isDragging: m.isDragging()
        }),
        end: onDragEnd
    }, [props.property, props.isDraggable]);

    useEffect(() => {
        if (isDragging) {
            props.onDragStart?.();
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isDragging])

    useEffect(() => {
        dragVisualRef(getEmptyImage());
    }, [dragVisualRef])

    const commands: ICommandButton[] = [

        {
            icon: {
                name: 'Trash',
                height: 1,
                width: 1,

            },
            label: 'Delete',
            canExecute: true,
            onClick: props.onDelete
        },
        {
            icon: {
                name: 'EditSolid12',
                height: 1,
                width: 1,

            },
            label: 'Edit',
            canExecute: true,
            onClick: props.onEdit
        },
    ]

    return (
        <PropertySlot
            id={props.property.spanId}
            onClick={(ev) => { ev.stopPropagation(); props.onClick() }}
            dragProps={{
                dragRef: dragRef,
                isDraggable: props.isDraggable
            }}
            selected={props.isSelected}
            commands={commands} >
            <TooltipHost content={props.property.label}>
                <CardLabel>
                    {props.property.label}
                </CardLabel>
            </TooltipHost>
            <CardTextContent
                text={props.property.value || (!!props.property.label ? `Click to add text to this property` : '')}
                background={props.isSelected && !!props.property.value ? chunkSelectedColor : undefined}
                maxLines={1}
                styles={!props.property.value ? { text: CardTextClassName.Empty } : {}} />
        </PropertySlot>
    )
}

const Container = styled.div<{ selected?: boolean }>`
    display: flex;
    flex-direction: column;
    cursor: pointer;
    width: 100%;
    ${props => !!props.selected && 'transform: scale(1.02)'};
`

const SlotRoot = styled.div.attrs(props => ({ className: props.className }))`
    display: flex;
    flex-direction: row;
    border-radius: 5px;
    background: ${primaryTheme.palette.white};
    box-shadow: ${cardShadow};
    margin: 5px; /* Account for box shadow */
    padding: 0px 5px;
    box-sizing: border-box;
    overflow: hidden;
`

const SlotContent = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
`

const ContentRoot = styled.div<{ maxHeight?: number }>`
    flex: 1;
    ${props => props.maxHeight !== undefined ? `max-height: ${props.maxHeight}px` : undefined};
    overflow: hidden;
    gap: 5px;
    padding: 8px 4px;
`

const CommandsContainer = styled(Stack)`
    margin: 5px;
`;

const PropertyItemIconContainer = styled.div`
    display: flex;
    align-items: center;
`

const IconWrapper = styled.div`
    font-size: 14px;
    display: flex;
    color: ${greyText};
    :hover {
        color: ${appContentColor};
    }
`

interface IPropertyItemDragProps {
    dragRef?: ConnectDragSource;
    isDraggable: boolean;
}

interface PropertySlotProps {
    id?: string;
    dragProps?: IPropertyItemDragProps;
    children?: JSX.Element | JSX.Element[];
    commands?: ICommandButton[];
    onClick?: (ev: any) => void;
    maxHeight?: number;
    selected?: boolean;
}

const PropertySlot = (props: PropertySlotProps): JSX.Element => {
    return (
        <Container id={props.id} ref={props.dragProps?.dragRef} selected={props.selected}>
            <SlotRoot>
                {
                    !!props.dragProps && (
                        <GrabHandleContainer disabled={!props.dragProps.isDraggable}>
                            <Icon iconName="CalculatorEqualTo" />
                        </GrabHandleContainer>
                    )
                }
                <SlotContent onClick={props.onClick}>
                    <ContentRoot maxHeight={props.maxHeight} >
                        {props.children}
                    </ContentRoot>
                </SlotContent>
                <CommandsContainer
                    tokens={{ childrenGap: 10 }}
                    horizontalAlign="center"
                    verticalAlign="space-evenly">
                    {
                        props.commands?.map(x => (
                            <PropertyItemIconContainer key={x.label}>
                                <IconWrapper onClick={(ev) => { ev.stopPropagation(); x.onClick?.(); }}>
                                    <Icon iconName={x.icon.name as string} title={x.label} />
                                </IconWrapper>
                            </PropertyItemIconContainer>
                        ))
                    }
                </CommandsContainer>
            </SlotRoot>
        </Container>
    )
}
